import { Component, Input, TemplateRef, ViewChild, ChangeDetectionStrategy, ContentChild } from '@angular/core';

@Component({
  selector: 'bs-tab',
  template: `
    <ng-template>
      <ng-content></ng-content>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BsTabComponent {
  /** The label text displayed on the tab */
  @Input() label: string = '';

  /** Optional tooltip text (HTML title attribute) */
  @Input() title?: string;

  /** Whether the tab is active */
  @Input() active: boolean = false;

  /** Whether the tab is disabled */
  @Input() disabled: boolean = false;

  /** Optional icon class for the tab */
  @Input() icon?: string;

  /** Optional badge text for the tab */
  @Input() badge?: string | number;

  /** Optional badge class for styling */
  @Input() badgeClass?: string;

  /** Optional custom ID for the tab */
  @Input() tabId?: string;

  /** Optional custom CSS class for the tab button */
  @Input() tabClass?: string;

  /** Optional aria-label for additional context */
  @Input() ariaLabel?: string;

  /** 
   * Optional custom hash identifier for URL synchronization.
   * When syncWithHash is enabled, this will be used instead of the numeric index.
   * Example: hashId="overview" results in URL hash #overview
   * If not provided, falls back to {idPrefix}-{index} format
   */
  @Input() hashId?: string;

  /** 
   * Enable lazy loading for this tab.
   * When true, content is only instantiated on first visit and remains in DOM.
   * When false (default), content is always in DOM but hidden/shown with display.
   */
  @Input() lazy: boolean = false;

  /** Internal flag to track if lazy content has been instantiated */
  _hasBeenInstantiated: boolean = false;

  /** Indicates if this is the first time the tab is becoming visible (useful for lazy content initialization) */
  _isFirstActivation: boolean = true;

  /** Direct content template (ng-content wrapped in ng-template) */
  @ViewChild(TemplateRef, { static: true }) content!: TemplateRef<any>;

  /** User-provided ng-template for true lazy loading (deferred initialization) */
  @ContentChild(TemplateRef, { static: false }) lazyContent?: TemplateRef<any>;
}
